// @ai-generated 2026-08-21T06:10:42+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { useFeatureFlags } from '@Contexts/featureFlags';
import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiContainerStockEntry } from '@Model';
import { ContainerStockRepository } from '@Repositories/Auftragsbearbeitung';
import { isOk } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

/** Shown whenever the stock stays unread and the server named no reason we could show instead. */
const STOCK_UNREADABLE = 'Der Containerbestand konnte nicht geladen werden.';

export interface ContainerStockRequest {
    siteId: string | undefined;
    customerId: string | undefined;
    /** A change in it re-reads the stock, which is what a booking looks like from here. */
    bookedContainers: number;
    /** When the order was last saved. A change re-reads the stock. */
    savedAt?: string;
}

export interface ContainerStockState {
    entries: ApiContainerStockEntry[];
    /** How many containers stand there. */
    total: number;
    isLoading: boolean;
    /** Why the stock could not be read, empty while it could. */
    failure: string;
    /** Whether a site and a customer are known, without which nothing is asked. */
    canBeRead: boolean;
    /** Asks again, for a caller that offers a way out of a failed read. */
    reload: () => void;
}

/**
 * The containers standing at a site, one entry per standing time.
 *
 * A failed read is reported rather than thrown, so losing the stock does not stop anyone from working
 * on the order.
 */
export function useContainerStock({
    siteId,
    customerId,
    bookedContainers,
    savedAt,
}: ContainerStockRequest): ContainerStockState {
    const containerStock = useAuftragRepository(ContainerStockRepository);
    const { isFeatureEnabled } = useFeatureFlags();

    // The route is feature-gated on the server and answers 404 with the flag off. Asking anyway
    // would trade a readable empty block for a failed request on every order mask.
    const canBeRead = isFeatureEnabled('CONTAINER_SERVICE') && !!siteId && !!customerId;

    const { data, isPending, refetch } = useQuery({
        queryKey: ['container-stock', siteId, customerId, bookedContainers, savedAt],
        queryFn: () => containerStock.getStockAtSite(siteId ?? '', customerId ?? '', undefined),
        enabled: canBeRead,
        // A new key must not blank the block while its answer is on the way, unless site or customer changed.
        placeholderData: (previous, previousQuery) => {
            const sameSite = previousQuery?.queryKey[1] === siteId && previousQuery?.queryKey[2] === customerId;

            return sameSite ? previous : undefined;
        },
    });

    // Stable, so a caller may depend on it without re-running its own memos every render.
    const reload = useCallback(() => void refetch(), [refetch]);

    if (data === undefined) {
        return { entries: [], total: 0, isLoading: canBeRead, failure: '', canBeRead, reload };
    }

    if (!isOk(data)) {
        return {
            entries: [],
            total: 0,
            isLoading: false,
            // A refused route answers with a problem document rather than a result, which carries no
            // messages at all — reaching into them is what took the whole order mask down.
            failure: data.messages?.[0]?.message ?? STOCK_UNREADABLE,
            canBeRead,
            reload,
        };
    }

    return {
        entries: data.data,
        total: data.data.length,
        isLoading: isPending,
        failure: '',
        canBeRead,
        reload,
    };
}
